import { useContext, useMemo, KeyboardEvent, useEffect, useRef } from "react";
import { useClickOutside } from '../../../hooks/ClickOutsideHook';
import { ISettingsCountryOption } from "../../../interfaces";
import { CountrySelectionOption } from "./../CountrySelectionOption";
import { SettingsContext } from "../../../contexts/SettingsContext";
import { handleKeyDown } from "../../../utils";
import { useListNavigation } from "../../../hooks/ListNavigation";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";

const options: ISettingsCountryOption[] = [
    { countryCode: "no", icon: "vismaicon vismaicon-flags vismaicon-norway", text: "NorwegianSettings" },
    { countryCode: "fi", icon: "vismaicon vismaicon-flags vismaicon-finland", text: "FinnishSettings" },
    { countryCode: "nl", icon: "vismaicon vismaicon-flags vismaicon-nederlands", text: "DutchSettings" },
];

export const GaCountrySelection = () => {
    const { defaultCountryCode, countryCode, setCountryCode } = useContext(SettingsContext);

    const [ref, showList, setShowList] = useClickOutside<HTMLDivElement>();
    const { activeItem, setActiveItem, clearState, focusNext, focusPrevious, focusFirst, focusLast } = useListNavigation<ISettingsCountryOption>(options);
    const optionsRefs = useRef<(HTMLButtonElement | null)[]>([]);

    const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
        if (showList === true)
            handleKeyDown(event, 'Tab', () => setShowList(false));
    }

    const handleTabKeyDownMemo = useMemo(() => handleTabKeyDown, [showList]);

    const handleKeyDownButton = (event: KeyboardEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        handleKeyDown(event, "Enter", () => {
            if (activeItem) {
                setCountryCode(activeItem.countryCode);
                setShowList(false);
            } else {
                setShowList(!showList);
                setActiveItem(options[0]);
            }
        });
        handleKeyDown(event, " ", () => {
            if (activeItem) {
                setCountryCode(activeItem.countryCode);
                setShowList(false);
            }
        });
        handleKeyDown(event, 'ArrowDown', () => {
            if (!showList) {
                setShowList(true);
                setActiveItem(options[0]);
            } else {
                focusNext();
            }
        });
        handleKeyDown(event, 'ArrowUp', () => {
            if (!showList) {
                setShowList(true);
                setActiveItem(options[options.length - 1]);
            } else {
                focusPrevious();
            }
        });
        handleKeyDown(event, 'Home', () => focusFirst());
        handleKeyDown(event, 'End', () => focusLast());
        handleKeyDown(event, 'Escape', () => setShowList(false));
        handleTabKeyDownMemo(event);
    }

    const handleClick = (option: ISettingsCountryOption) => {
        setCountryCode(option.countryCode);
        setShowList(false);
    };

    useEffect(() => {
        if (showList && activeItem) {
            const idx = options.findIndex(o => o.countryCode === activeItem.countryCode);
            if (idx >= 0 && optionsRefs.current[idx]) {
                optionsRefs.current[idx]?.focus();
            }
        }
    }, [activeItem, showList, options]);

    useEffect(() => {
        if (showList === false)
            setActiveItem(undefined);
    }, [showList]);

    return (
        <div ref={ref} className="ga-form-field ar-ga-dropdown ar-ga-dropdown-countrycode">
            <button
                type="button"
                className="ga-select"
                onClick={() => {
                    setShowList(!showList);
                    if (!showList) setActiveItem(options[0]);
                }}
                onKeyDown={handleKeyDownButton}
                aria-haspopup="listbox"
                aria-expanded={showList}
            >
                <CountrySelectionOption
                    option={options.find(o => o.countryCode === countryCode) ?? options[0]}
                    defaultCountryCode={defaultCountryCode} />
                {!showList && <ChevronDownIcon className="ga-select__arrow" size={16} />}
                {showList && <ChevronUpIcon className="ga-select__arrow" size={16} />}
            </button>
            {showList &&
                <div className="ga-dropdown" role="listbox" aria-activedescendant={activeItem?.countryCode}>
                    {options.map((o, idx) => (
                        <button
                            key={o.countryCode}
                            ref={el => { optionsRefs.current[idx] = el; }}
                            className={`ga-dropdown__item ${o.countryCode === countryCode ? "ga-dropdown__item--selected" : ""} ${activeItem?.countryCode === o.countryCode ? "ga-dropdown__item--active" : ""}`}
                            onClick={() => handleClick(o)}
                            type="button"
                            tabIndex={activeItem?.countryCode === o.countryCode ? 0 : -1}
                            aria-selected={countryCode === o.countryCode}
                            onKeyDown={handleKeyDownButton}
                        >
                            <div className="ga-dropdown__item-label">
                                <CountrySelectionOption
                                    option={o}
                                    defaultCountryCode={defaultCountryCode} />
                            </div>
                        </button>
                    ))}
                </div>
            }
        </div>
    );
};